import { type FC, useMemo } from "react";
import { Layout } from "@viagogo/core/ui";
import { useFeatureParticipationRecorder, useFeatureTernary } from "@viagogo/feature";
import { GridType, type VenueEventListViewModel } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { getLocationCookieOptions, useLocationContext } from "src/shared/context/location-context";
import { FilterTypes } from "src/shared/enums/enums";
import { useScrollToggle } from "src/shared/hooks/useScrollToggle";
import { useWindowLocation } from "src/shared/hooks/useWindowLocation";
import type { Dates } from "src/types/shared/cookies";
import { useVenuePageModelData } from "src/website-apps/responsive/stubhub-venue/hooks/useVenuePageModelData";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { FavoritesProvider } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContext";
import { HeroBannerFavoritesDataContext } from "src/website-apps/shared/components/hero-banner";
import { GlobalRadiusInMeters } from "src/website-apps/shared/constants/grid-radius";
import { useDateCookieOptions } from "src/website-apps/shared/context/date-context";
import { EventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useGridsReducer } from "src/website-apps/shared/hooks/useGridsReducer";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { pageWrapperRecipe } from "src/website-apps/shared/styles/shared-styles";
import { getDateQueryParams } from "src/website-apps/shared/utils/getDateQueryParams";
import { getFilterQueryParams } from "src/website-apps/shared/utils/getFilterQueryParams";
import { VenueLayoutV2Body } from "./VenueLayoutV2Body";
import { VenueLayoutV2Footer } from "./VenueLayoutV2Footer";
import { VenueLayoutV2Header } from "./VenueLayoutV2Header";

const resolveInitialFavoritesState = (model: VenueEventListViewModel) => {
  const events = [
    ...Object.values(model.eventGrids).flatMap((payload) => payload.items ?? []),
    ...(model.trendingEvents?.items ?? []),
  ];
  const eventFavorites = Object.fromEntries(events.map((curr) => [curr.eventId ?? -1, curr.isFavorite]));
  return { [ENTITY_TYPES.EVENT]: { ...eventFavorites } };
};

interface Props {
  dates?: Maybe<Dates>;
}

/**
 * VenueLayoutV2 — Variant layout for TMVenueFirstDiscovery (experiments 102524/102525).
 *
 * Based on VenueLayoutV1 with:
 * - Header replaced by the new VenueHeroHeader (CWP-2476)
 * - Trending carousel above the event list (CWP-2472)
 * - FiltersAndGridsManager replaced by performer-grouped event list (CWP-2473)
 * - Right panel with map card (CWP-2472)
 */
export const VenueLayoutV2: FC<Props> = ({ dates }) => {
  const { search } = useWindowLocation();
  const now = useStableNow();
  const appContext = useAppContext();
  const { env } = appContext;
  const model = useVenuePageModelData();
  const initialFavorites = resolveInitialFavoritesState(model);
  const cookieOptions = useMemo(() => getLocationCookieOptions(appContext), [appContext]);
  const [location] = useLocationContext(cookieOptions);
  const dateCookieOptions = useDateCookieOptions();
  const execDateWithExpiration = useFeatureTernary(FeatureSetEnum.EventListExpireDateFilter);
  const dateWithExpiration =
    execDateWithExpiration({
      control: () => false,
      variant: () => true,
    }) ?? undefined;
  const datesQueryParams = getDateQueryParams(env, search, now, dateWithExpiration, dateCookieOptions);
  const filters = getFilterQueryParams(search);

  const [mobileMenuOpen, handleOnMobileMenuToggle] = useScrollToggle();

  useFeatureParticipationRecorder(FeatureSetEnum.TMGlobalTrendingOnAncillaryPages)();

  const imageUrl = model.venueImageUrl ?? null;

  const reducerOptions = useMemo(
    () => ({
      primaryGrid: model.eventGrids[GridType.PrimaryRadius],
      primaryFromRadius: 0,
      primaryToRadius: GlobalRadiusInMeters,
      restGrid: model.eventGrids[GridType.Rest],
      restFromRadius: GlobalRadiusInMeters,
      restToRadius: null,
      method: "GetFilteredEvents",
      venueId: model.venueId,
      location,
      dates: datesQueryParams,
      sortBy: filters.sortBy,
      gridFilterType: model.isParkingVenue ? FilterTypes.PARKING : filters.gridFilterType,
      eventViewType: filters.eventViewType,
      topLevelCategoryId: filters.topLevelCategoryId ?? undefined,
      medianPriceBucketFilter: filters.medianPriceBucketFilter,
      calendarEvents: model.calendarEvents,
    }),
    [
      datesQueryParams,
      filters.eventViewType,
      filters.gridFilterType,
      filters.medianPriceBucketFilter,
      filters.sortBy,
      filters.topLevelCategoryId,
      location,
      model.calendarEvents,
      model.eventGrids,
      model.isParkingVenue,
      model.venueId,
    ],
  );
  const gridsContext = useGridsReducer(reducerOptions);
  const [gridsState] = gridsContext;
  const eventCount = (gridsState.grids ?? []).reduce((total, grid) => total + (grid.totalCount ?? 0), 0);

  const heroBannerFavoritesDataContext = useMemo(
    () => ({
      entityType: ENTITY_TYPES.VENUE,
      entityId: model.venueId,
    }),
    [model.venueId],
  );

  return (
    <div className={pageWrapperRecipe({ mobileMenu: mobileMenuOpen ? "open" : "closed" })}>
      <EventsGridContext.Provider value={gridsContext}>
        <FavoritesProvider data={initialFavorites}>
          <HeroBannerFavoritesDataContext.Provider value={heroBannerFavoritesDataContext}>
            <Layout
              header={
                <VenueLayoutV2Header
                  model={model}
                  imageUrl={imageUrl}
                  eventCount={eventCount}
                  mobileMenuOpen={mobileMenuOpen}
                  onMobileMenuToggle={handleOnMobileMenuToggle}
                />
              }
              body={<VenueLayoutV2Body model={model} dates={dates} />}
              footer={<VenueLayoutV2Footer model={model} />}
            />
          </HeroBannerFavoritesDataContext.Provider>
        </FavoritesProvider>
      </EventsGridContext.Provider>
    </div>
  );
};
